import { useEffect, useState } from 'react';
import { AlertCircle, Loader2, Plus } from 'lucide-react';
import { Select } from '@/components/ui/Select';

// Współdzielony konfigurator avatara UGC: wyklikane cechy → automatyczny prompt
// (edytowalny). Używany w katalogu avatarów (LabAvatarsPage) i w kroku
// "Nowy avatar" modalu produkcji. Reguły naturalności UGC dokleja backend.

export interface AvatarDraft {
  name: string;
  gender: string;
  ageRange: string;
  traits: Record<string, string>;
  prompt: string;
  isSystem?: boolean;
}

export const GENDER_OPTIONS = [
  { value: 'female', label: 'Kobieta', prompt: 'woman' },
  { value: 'male', label: 'Mężczyzna', prompt: 'man' },
];

export const AGE_OPTIONS = [
  { value: '18-25', label: '18-25 lat', prompt: 'in their early 20s' },
  { value: '25-35', label: '25-35 lat', prompt: 'around 30 years old' },
  { value: '35-45', label: '35-45 lat', prompt: 'around 40 years old' },
  { value: '45-55', label: '45-55 lat', prompt: 'around 50 years old' },
  { value: '55+', label: '55+ lat', prompt: 'in their 60s' },
];

const HAIR_OPTIONS = [
  { value: '', label: 'Dowolne', prompt: '' },
  { value: 'blond-dlugie', label: 'Blond, długie', prompt: 'long blonde hair' },
  { value: 'blond-krotkie', label: 'Blond, krótkie', prompt: 'short blonde hair' },
  { value: 'braz-dlugie', label: 'Brązowe, długie', prompt: 'long brown hair' },
  { value: 'braz-krotkie', label: 'Brązowe, krótkie', prompt: 'short brown hair' },
  { value: 'czarne-dlugie', label: 'Czarne, długie', prompt: 'long black hair' },
  { value: 'czarne-krotkie', label: 'Czarne, krótkie', prompt: 'short black hair' },
  { value: 'rude', label: 'Rude', prompt: 'red hair' },
  { value: 'siwe', label: 'Siwe', prompt: 'gray hair' },
  { value: 'lysy', label: 'Łysy / ogolona', prompt: 'shaved head' },
];

const BUILD_OPTIONS = [
  { value: '', label: 'Przeciętna', prompt: '' },
  { value: 'szczupla', label: 'Szczupła', prompt: 'slim build' },
  { value: 'wysportowana', label: 'Wysportowana', prompt: 'athletic build' },
  { value: 'plus-size', label: 'Plus size', prompt: 'plus-size build' },
];

const OUTFIT_OPTIONS = [
  { value: 'casual', label: 'Casualowy', prompt: 'casual everyday clothes' },
  { value: 'elegancki', label: 'Elegancki', prompt: 'smart elegant outfit' },
  { value: 'sportowy', label: 'Sportowy', prompt: 'sporty athleisure outfit' },
  { value: 'domowy', label: 'Domowy', prompt: 'comfortable homewear' },
];

const SETTING_OPTIONS = [
  { value: 'kuchnia', label: 'Jasna kuchnia', prompt: 'in a bright modern kitchen' },
  { value: 'salon', label: 'Salon', prompt: 'in a cozy living room' },
  { value: 'lazienka', label: 'Łazienka', prompt: 'in a clean bathroom' },
  { value: 'sypialnia', label: 'Sypialnia', prompt: 'in a bright bedroom' },
  { value: 'biuro', label: 'Biuro', prompt: 'in a home office' },
  { value: 'samochod', label: 'Samochód', prompt: 'sitting in a car' },
  { value: 'ulica', label: 'Na zewnątrz / ulica', prompt: 'outdoors on a city street' },
  { value: 'silownia', label: 'Siłownia', prompt: 'at the gym' },
];

function buildAvatarPrompt(args: {
  gender: string;
  ageRange: string;
  hair: string;
  build: string;
  outfit: string;
  setting: string;
  extras: string;
}): string {
  const gender = GENDER_OPTIONS.find(item => item.value === args.gender)?.prompt || 'person';
  const age = AGE_OPTIONS.find(item => item.value === args.ageRange)?.prompt || '';
  const hair = HAIR_OPTIONS.find(item => item.value === args.hair)?.prompt || '';
  const build = BUILD_OPTIONS.find(item => item.value === args.build)?.prompt || '';
  const outfit = OUTFIT_OPTIONS.find(item => item.value === args.outfit)?.prompt || '';
  const setting = SETTING_OPTIONS.find(item => item.value === args.setting)?.prompt || '';
  const extras = args.extras.trim();
  const person = [build, gender, age].filter(Boolean).join(' ');
  const details = [hair, outfit ? `wearing ${outfit}` : '', extras].filter(Boolean).join(', ');
  return [
    `Ultra-realistic UGC-style photo of a ${person}`,
    details,
    setting,
    'natural lighting, looking straight at the camera, authentic smartphone selfie framing, vertical portrait',
  ].filter(Boolean).join(', ');
}

interface AvatarCreatorFormProps {
  submitting: boolean;
  submitLabel: string;
  showSystemToggle?: boolean;
  error?: string | null;
  onSubmit: (draft: AvatarDraft) => void;
  idPrefix?: string;
}

export function AvatarCreatorForm({ submitting, submitLabel, showSystemToggle, error, onSubmit, idPrefix = 'avatar' }: AvatarCreatorFormProps) {
  const [name, setName] = useState('');
  const [gender, setGender] = useState('female');
  const [ageRange, setAgeRange] = useState('25-35');
  const [hair, setHair] = useState('');
  const [build, setBuild] = useState('');
  const [outfit, setOutfit] = useState('casual');
  const [setting, setSetting] = useState('salon');
  const [extras, setExtras] = useState('');
  const [isSystem, setIsSystem] = useState(Boolean(showSystemToggle));
  const [prompt, setPrompt] = useState(() => buildAvatarPrompt({ gender: 'female', ageRange: '25-35', hair: '', build: '', outfit: 'casual', setting: 'salon', extras: '' }));
  const [promptTouched, setPromptTouched] = useState(false);

  // Prompt buduje się z cech dopóki user nie edytował go ręcznie
  useEffect(() => {
    if (promptTouched) return;
    setPrompt(buildAvatarPrompt({ gender, ageRange, hair, build, outfit, setting, extras }));
  }, [gender, ageRange, hair, build, outfit, setting, extras, promptTouched]);

  const selectField = (label: string, id: string, value: string, onChange: (v: string) => void, options: Array<{ value: string; label: string }>) => (
    <div>
      <label htmlFor={`${idPrefix}-${id}`} className="text-xs font-medium text-text-secondary mb-1.5 block">{label}</label>
      <Select id={`${idPrefix}-${id}`} value={value} onChange={e => onChange(e.target.value)}>
        {options.map(option => (
          <option key={option.value} value={option.value}>{option.label}</option>
        ))}
      </Select>
    </div>
  );

  return (
    <div className="space-y-4">
      <div>
        <label htmlFor={`${idPrefix}-name`} className="text-xs font-medium text-text-secondary mb-1.5 block">Nazwa avatara</label>
        <input
          id={`${idPrefix}-name`}
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder='np. "Kasia 30 casual"'
          className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
        {selectField('Płeć', 'gender', gender, setGender, GENDER_OPTIONS)}
        {selectField('Wiek', 'age', ageRange, setAgeRange, AGE_OPTIONS)}
        {selectField('Włosy', 'hair', hair, setHair, HAIR_OPTIONS)}
        {selectField('Budowa', 'build', build, setBuild, BUILD_OPTIONS)}
        {selectField('Styl ubioru', 'outfit', outfit, setOutfit, OUTFIT_OPTIONS)}
        {selectField('Otoczenie', 'setting', setting, setSetting, SETTING_OPTIONS)}
      </div>

      <div>
        <label htmlFor={`${idPrefix}-extras`} className="text-xs font-medium text-text-secondary mb-1.5 block">
          Znaki szczególne (opcjonalnie)
        </label>
        <input
          id={`${idPrefix}-extras`}
          type="text"
          value={extras}
          onChange={e => setExtras(e.target.value)}
          placeholder="np. glasses, freckles, small tattoo on forearm (po angielsku)"
          className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40"
        />
      </div>

      <div>
        <label htmlFor={`${idPrefix}-prompt`} className="text-xs font-medium text-text-secondary mb-1.5 block">
          Prompt (opis dla generatora obrazu)
        </label>
        <textarea
          id={`${idPrefix}-prompt`}
          value={prompt}
          onChange={e => {
            setPrompt(e.target.value);
            setPromptTouched(true);
          }}
          rows={4}
          maxLength={1000}
          className="w-full text-xs bg-surface-hover border border-border rounded-lg px-3 py-2 text-text-primary placeholder:text-text-disabled focus:outline-none focus:border-accent-primary/40 font-mono"
        />
        <div className="flex items-center justify-between mt-1">
          {promptTouched ? (
            <button onClick={() => setPromptTouched(false)} className="text-[10px] text-accent-info hover:underline">
              Wróć do promptu z cech
            </button>
          ) : <span />}
          <span className="text-[10px] text-text-disabled">{prompt.length}/1000</span>
        </div>
      </div>

      {showSystemToggle && (
        <label className="flex items-start gap-2 cursor-pointer select-none">
          <input type="checkbox" checked={isSystem} onChange={e => setIsSystem(e.target.checked)} className="mt-0.5 accent-current" />
          <span>
            <span className="text-xs font-medium text-text-primary block">Dodaj do katalogu systemowego</span>
            <span className="text-[11px] text-text-muted block mt-0.5">Predefiniowany avatar widoczny dla wszystkich kont (zarządza superadmin).</span>
          </span>
        </label>
      )}

      {error && (
        <div className="flex items-center gap-2 text-[11px] text-red-400">
          <AlertCircle className="w-3.5 h-3.5 shrink-0" /> {error}
        </div>
      )}

      <button
        onClick={() => onSubmit({
          name: name.trim(),
          gender,
          ageRange,
          traits: { hair, build, outfit, setting, extras: extras.trim() },
          prompt: prompt.trim(),
          ...(showSystemToggle && { isSystem }),
        })}
        disabled={submitting || !name.trim() || prompt.trim().length < 10}
        className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-accent-primary rounded-lg hover:bg-accent-primary/90 transition-all disabled:opacity-40"
      >
        {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Plus className="w-3.5 h-3.5" />}
        {submitLabel}
      </button>
    </div>
  );
}
